import React, { useState, useRef, ReactElement } from 'react';
import {
  isBefore,
  isAfter,
  format,
  startOfMonth,
  addMonths,
  subDays,
  addDays,
  isWithinInterval,
  eachDayOfInterval,
  endOfMonth,
  addYears,
} from 'date-fns';
import { Button, Col, Row } from 'antd';
import { ReactComponent as DoublePrev } from '../../../../assets/images/svg/double_arrow_left.svg';
import { ReactComponent as DoubleNext } from '../../../../assets/images/svg/double_arrow_right.svg';
import { ReactComponent as SinglePrev } from '../../../../assets/images/svg/single_arrow_left.svg';
import { ReactComponent as SingleNext } from '../../../../assets/images/svg/single_arrow_right.svg';
import './DatePicker.less';
import {
  DATE_FORMAT_MMMM_YYY,
  DATE_FORMAT_MM_DD_YYYY,
  WEEK_DAYS,
} from '../../../constants/commonConstants';
import { REPORTING_CONSTANTS } from '../../../../modules/reportingAndAnalytics/reportingUtils/reportingAndAnalyticsConstants';

interface ReportingDatePickerProps {
  initialStartDate?: Date;
  initialEndDate?: Date;
  limit: number;
  leftLimit: number;
  rightLimit: number;
  setIsDatePickerOpen: (open: boolean) => void;
  setSelect: (start: string, end: string) => void;
  disablefutureDates?: boolean;
  disabledStartDate?: boolean;
  handleDateOk?: any;
  confirmBtnName?: string;
  isModalCalander?: boolean;
}

const formatSafe = (date: Date | undefined, fmt: string): string =>
  date ? format(date, fmt) : '';
const isSameDaySafe = (a: Date | undefined, b: Date | undefined): boolean =>
  a && b
    ? format(a, DATE_FORMAT_MM_DD_YYYY) === format(b, DATE_FORMAT_MM_DD_YYYY)
    : false;

const ReportingDatePicker: React.FC<ReportingDatePickerProps> = ({
  initialStartDate,
  initialEndDate,
  limit,
  leftLimit,
  rightLimit,
  setIsDatePickerOpen,
  setSelect,
  disablefutureDates = false,
  disabledStartDate = false,
  handleDateOk,
  confirmBtnName,
  isModalCalander,
}: any): ReactElement => {
  const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>(
    initialStartDate,
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(
    initialEndDate,
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(
    initialStartDate ? startOfMonth(initialStartDate) : startOfMonth(new Date()),
  );

  const daysContainerRef = useRef<HTMLDivElement>(null);
  const now = new Date();
  const leftLimitDate = subDays(now, leftLimit);
  const rightLimitDate = addDays(now, rightLimit);

  const handleApply = (): void => {
    setSelect(
      formatSafe(
        selectedStartDate,
        REPORTING_CONSTANTS.REPORTING_DISPLAY_FORMAT,
      ),
      formatSafe(selectedEndDate, REPORTING_CONSTANTS.REPORTING_DISPLAY_FORMAT),
    );
    handleDateOk?.();
    setIsDatePickerOpen(false);
  };

  /** Disable logic for days */
  const checkDisabledDay = (current: Date): boolean => {
    if (disablefutureDates && isAfter(current, now)) {
      return true;
    }
    if (isBefore(current, leftLimitDate) || isAfter(current, rightLimitDate)) {
      return true;
    }

    if (selectedStartDate && !selectedEndDate) {
      // After selecting start date, disable all days before it
      if (isBefore(current, selectedStartDate)) {
        return true;
      }
      // Disable if exceeds limit
      if (isAfter(current, addDays(selectedStartDate, limit))) {
        return true;
      }
    }
    return false;
  };

  const handleMonthChange = (numMonths: number): void => {
    setCurrentMonth(addMonths(currentMonth, numMonths));
  };
  const handleYearChange = (numYears: number): void => {
    setCurrentMonth(addYears(currentMonth, numYears));
  };

  const renderHeader = (): JSX.Element => (
    <div className="datePickerHeader row flex-middle">
      <span className="column col-start">
        <span className="icon" onClick={(): void => handleYearChange(-1)}>
          <DoublePrev />
        </span>
        <span className="icon" onClick={(): void => handleMonthChange(-1)}>
          <SinglePrev />
        </span>
      </span>
      <span className="column col-center">
        <span>{formatSafe(currentMonth, DATE_FORMAT_MMMM_YYY)}</span>
      </span>
      <span className="column col-end">
        <span className="icon" onClick={(): void => handleMonthChange(1)}>
          <SingleNext />
        </span>
        <span className="icon" onClick={(): void => handleYearChange(1)}>
          <DoubleNext />
        </span>
      </span>
    </div>
  );

  const handleDayClick = (day: Date): void => {
    if (checkDisabledDay(day)) {
      return;
    }

    if (disabledStartDate) {
      setSelectedEndDate(day);
      setSelect(
        formatSafe(selectedStartDate, DATE_FORMAT_MM_DD_YYYY),
        formatSafe(day, DATE_FORMAT_MM_DD_YYYY),
      );
      return;
    }

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(day);
      setSelectedEndDate(undefined);
      setSelect(formatSafe(day, DATE_FORMAT_MM_DD_YYYY), '');
      return;
    }

    if (selectedStartDate && !selectedEndDate) {
      setSelectedEndDate(day);
      setSelect(
        formatSafe(selectedStartDate, DATE_FORMAT_MM_DD_YYYY),
        formatSafe(day, DATE_FORMAT_MM_DD_YYYY),
      );
    }
  };

  const renderDays = (): JSX.Element => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    return (
      <>
        <div className="days row">
          {WEEK_DAYS.map((dayInfo: any): ReactElement  => (
            <div className="col cell daysHeader" key={dayInfo.fullName}>
              {dayInfo.day}
            </div>
          ))}
        </div>
        <div className="days row" ref={daysContainerRef}>
          {days.map((day: Date): ReactElement  => {
            const isSelecteStartDate = isSameDaySafe(day, selectedStartDate);
            const isSelectedEndDate = isSameDaySafe(day, selectedEndDate);
            const inRange =
              selectedStartDate &&
              selectedEndDate &&
              isAfter(selectedEndDate, selectedStartDate) &&
              isWithinInterval(day, {
                start: selectedStartDate,
                end: selectedEndDate,
              });
            const disabled = checkDisabledDay(day);
            const isCurrent =
              formatSafe(new Date(), DATE_FORMAT_MM_DD_YYYY) ===
              formatSafe(day, DATE_FORMAT_MM_DD_YYYY);

            const className = [
              'col',
              'cell',
              isSelecteStartDate && 'selected',
              isSelectedEndDate && 'selected',
              inRange && 'dateRangeElements',
              disabled && 'disabledDates',
              isCurrent && 'currentDate',
            ]
              ?.filter(Boolean)
              ?.join(' ');

            return (
              <div
                key={day.toString()}
                className={className}
                onClick={(): any => !disabled && handleDayClick(day)}
              >
                {format(day, 'd')}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <Row gutter={24} className="reportingDatePickerComponent">
      <Col md={24} className="reportingDatePickerCalender">
        {renderHeader()}
        {renderDays()}
      </Col>
      <div
        className={`datePickerFooter ml-190 ${
          isModalCalander ? 'reporting-date-picker-btn' : ''
        }`}
      >
        <Button
          size="small"
          className="datepicker-apply"
          type="primary"
          onClick={handleApply}
          disabled={!selectedStartDate || !selectedEndDate}
        >
          {confirmBtnName ?? 'OK'}
        </Button>
      </div>
    </Row>
  );
};

export default ReportingDatePicker;
